import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);

export { dayjs };

/**
 * Centralized UTC Date/Time Utilities for Stradix Application
 *
 * PURPOSE: Ensure all date/datetime values sent to backend are in UTC
 * BACKEND EXPECTATION: "YYYY-MM-DD HH:mm:ss" format in UTC
 */

export const BACKEND_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

/**
 * Converts a Date object to UTC string in backend format
 * @param date - JavaScript Date object (in user's local timezone)
 * @param dateOnly - If true, sets time to 00:00:00 UTC (for date-only fields)
 * @returns String in "YYYY-MM-DD HH:mm:ss" format (UTC)
 */
export function formatDateToUtc(date: Date, dateOnly: boolean = false): string {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    if (process.env.NODE_ENV === 'development' && date) {
      console.warn('[formatDateToUtc] Invalid date provided:', date);
    }
    return '';
  }

  let utcDate = dayjs(date).utc();

  if (dateOnly) {
    // For date-only fields, use midnight UTC
    utcDate = dayjs(date).startOf('day').utc();
  }

  return utcDate.format(BACKEND_DATETIME_FORMAT);
}

/**
 * Parses a UTC datetime string from backend and converts to local Date
 * @param utcString - String in "YYYY-MM-DD HH:mm:ss" format (UTC)
 * @returns JavaScript Date object in user's local timezone
 */
export function parseUtcToLocal(utcString: string): Date | null {
  if (!utcString) return null;

  // Backend sends UTC strings without 'Z' suffix
  const parsed = dayjs.utc(utcString, BACKEND_DATETIME_FORMAT);

  if (!parsed.isValid()) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[parseUtcToLocal] Invalid UTC string provided:', utcString);
    }
    return null;
  }

  return parsed.local().toDate();
}

/**
 * Validates if a date is valid
 */
export function isValidDate(date: any): boolean {
  return date instanceof Date && !isNaN(date.getTime());
}

/**
 * Parses a date value as a raw local date at midnight, skipping UTC timezone conversion.
 * Use for standalone DatePicker fields where the backend date should be displayed as-is
 * (no timezone shift). Strings are parsed as local calendar dates at 00:00:00.
 * @param value - Date object or string in "YYYY-MM-DD" or "YYYY-MM-DD HH:mm:ss" format
 * @returns JavaScript Date object at midnight local time, or null
 */
export function parseRawDateString(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    // Use local getters to avoid any UTC conversion
    return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 0, 0, 0, 0);
  }
  // Extract "YYYY-MM-DD" from "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DD"
  const datePart = String(value).split(' ')[0];
  const parts = datePart.split('-');
  if (parts.length !== 3) return null;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Date month is 0-indexed
  const day = parseInt(parts[2], 10);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  // new Date(y, m, d, ...) always creates local midnight — no UTC conversion
  return new Date(year, month, day, 0, 0, 0, 0);
}

/**
 * Formats a Date object as a raw backend date string "YYYY-MM-DD 00:00:00"
 * using local time getters. No UTC conversion — the calendar date is preserved as-is.
 * Use for standalone DatePicker fields that skip timezone conversion (paired with parseRawDateString).
 * @param date - JavaScript Date object
 * @returns String in "YYYY-MM-DD 00:00:00" format (local calendar date, midnight)
 */
export function formatRawDateString(date: Date): string {
  if (!date || isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day} 00:00:00`;
}

/**
 * Checks whether a raw date-only string ("YYYY-MM-DD..." from formatRawDateString/parseRawDateString)
 * falls before today's local calendar date. No UTC conversion — compares local calendar dates only.
 * Use for standalone DatePicker fields that skip timezone conversion.
 * @param dateOnly - String in "YYYY-MM-DD" or "YYYY-MM-DD HH:mm:ss" format
 * @returns true if the date is strictly before today (local time)
 */
export function isPastRawDate(dateOnly: string): boolean {
  if (!dateOnly) return false;
  const datePart = dateOnly.slice(0, 10);
  const today = new Date();
  const todayDatePart = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return datePart < todayDatePart;
}

/**
 * DEPRECATED: Legacy wrapper for gradual migration
 * Use formatDateToUtc instead
 */
export function formatDate(date: Date): string {
  console.warn('formatDate is deprecated. Use formatDateToUtc instead');
  return formatDateToUtc(date, false);
}
